import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import {
  Badge,
  Grid,
  Loader,
  Center,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { IconArrowLeft } from '@tabler/icons-react';
import { generateRandomString } from '@/helpers/string';
import Banner from '@/components/Banner';
import Page from '@/components/Page';
import { routeWithParams } from '@/helpers/routes';
import routes from '@/routes/index';
import { getMutationResult } from '@/helpers/query';
import { useGetUserStatusMutation } from '@/services/api';
import Stats from '@/components/Stats';
import TableSimple from '@/components/TableSimple';
import Box from '@/components/Box';
import { STATUS_COLOR, STATUS } from '@/helpers/enums';
import CreateNewImage from '@/assets/images/createNew.svg?react'; // eslint-disable-line import/no-unresolved

const Main = () => {
  const [openOffers, setOpenOffers] = useState(null);
  const [closedOffers, setClosedOffers] = useState(null);
  const [sentOffers, setSentOffers] = useState(null);
  const [lastFiveOffers, setLastFiveOffers] = useState([]);
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const id = generateRandomString();
  const { name, role, config } = useSelector((state) => state.user);
  const { code } = role;
  const nameToShow = code === '5' ? config.name : name;
  const [apiGetUserStatus] = useGetUserStatusMutation();
  const formattedDate = (date) => dayjs(date).format('DD/MM/YYYY HH:mm');
  const { refreshToken } = useSelector((state) => state.user);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const { data: result } = await getMutationResult({ refreshToken }, apiGetUserStatus);
        const { success, data: resultData } = result;

        if (success) {
          setOpenOffers(resultData.open);
          setClosedOffers(resultData.closed);
          setSentOffers(resultData.sentToClient);
          setLastFiveOffers(resultData.lastFiveOffers);
        } else {
          setIsError(true);
        }
      } catch (error) {
        setIsError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableDataBody = lastFiveOffers.map(
    (offer) => [<Badge
      key={offer._id}
      variant="light"
      color={STATUS_COLOR[offer.status?.name] || 'gray'}
    >{STATUS[offer.status?.name] || 'Unknown'}
    </Badge>,
    offer.idNumber,
    offer.fullName,
    formattedDate(offer.createdAt)],
  );

  const tableData = {
    head: [
      t('pages.main.table.head.status'),
      t('pages.main.table.head.uid'),
      t('pages.main.table.head.fullName'),
      t('pages.main.table.head.createdAt'),
    ],
    body: tableDataBody,
  };

  const handleCreateClick = () => {
    navigate(routeWithParams(routes.newProposal.pages[0].path, { id }));
  };

  if (loading) {
    return <Center><Loader /></Center>;
  }

  return (
    <Page title={t('pages.main.title')} hideHelp hideNews>
      <Page.Head
        title={t('pages.main.head.title', { name: nameToShow })}
        subtitle={t('pages.main.head.subtitle')}
      />
      <Page.Section>
        {loading ? <Loader /> : <Grid>
          <Grid.Col span={4}>
            <Stats
              title={t('pages.main.stats.offers.title')}
              subtitle={t('pages.main.stats.offers.subtitle')}
              value={openOffers}
            />
          </Grid.Col>

          <Grid.Col span={4}>
            <Stats
              title={t('pages.main.stats.waiting.title')}
              subtitle={t('pages.main.stats.waiting.subtitle')}
              value={closedOffers}
            />
          </Grid.Col>

          <Grid.Col span={4}>
            <Stats
              title={t('pages.main.stats.sent.title')}
              subtitle={t('pages.main.stats.sent.subtitle')}
              value={sentOffers}
            />
          </Grid.Col>
        </Grid>
        }

      </Page.Section>
      <Page.Section>
        <Grid>
          <Grid.Col span={4}>
            <Banner
              height="md"
              title={t('pages.main.banner.title')}
              avatar={<CreateNewImage />}
              onClick={handleCreateClick}
              action={t('pages.main.banner.action')}
              buttonProps={{
                rightSection: <IconArrowLeft />,
              }}
            />
          </Grid.Col>
          <Grid.Col span={8}>
            <Box
              withBackground
              title={t('pages.main.table.title')}
              footer={<Link to={routes.proposals.path}>{t('pages.main.table.footer')} <IconArrowLeft size={14} /></Link>}
            >
              {loading ? <Loader /> : <TableSimple data={tableData} />}
            </Box>
          </Grid.Col>
        </Grid>
      </Page.Section>
    </Page>
  );
};

export default Main;
